import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeroCarousel from '../components/HeroCarousel';
import BlogCard from '../components/BlogCard';
import SubscribeForm from '../components/SubscribeForm';
import PixieDust from '../components/PixieDust';
import { Reveal } from '../utils/useReveal';
import { postExcerpt, postTitle } from '../utils/postContent';
import './Home.css';

const S3_BASE = 'https://chrystal-mousebound-site-prod-113462084414-ca-central-1.s3.ca-central-1.amazonaws.com';

const ArrowRight = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" width="15" height="15" aria-hidden="true">
    <line x1="5" y1="12" x2="19" y2="12"/>
    <polyline points="12 5 19 12 12 19"/>
  </svg>
);

const Home = () => {
  const [popularPosts, setPopularPosts] = useState([]);
  const [featuredPost, setFeaturedPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (window.location.hash === '#subscribe') {
      setTimeout(() => {
        document.getElementById('subscribe')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 500);
    }
  }, []);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const manifestRes = await fetch(`${S3_BASE}/blog-posts/manifest.json`);
        if (!manifestRes.ok) throw new Error('Manifest not found');
        const { posts: slugs } = await manifestRes.json();

        const settled = await Promise.allSettled(
          slugs.map(slug =>
            fetch(`${S3_BASE}/blog-posts/metadata/${slug}.json`).then(r => r.json())
          )
        );

        const posts = settled
          .filter(r => r.status === 'fulfilled')
          .map(r => r.value)
          .sort((a, b) => new Date(b.date) - new Date(a.date));

        setFeaturedPost(posts.find(p => p.featured) || null);
        setPopularPosts(posts.filter(p => !p.featured).slice(0, 3));
      } catch (error) {
        console.error('Error fetching blog posts:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPosts();
  }, []);

  return (
    <div className="home">
      <HeroCarousel />

      {/* Featured Post */}
      {!isLoading && featuredPost && (
        <section className="featured-post" aria-label="Featured article">
          <PixieDust count={10} className="pixie-dust--short" />
          <div className="container">
            <Reveal className="featured-post-content shimmer-sweep">
              <div className="featured-post-image">
                <img src={featuredPost.image} alt={`Cover for ${postTitle(featuredPost.title)}`} loading="eager" />
                <div className="featured-badge" aria-label="Featured article">★ Featured</div>
              </div>
              <div className="featured-post-text">
                <div className="featured-eyebrow">
                  <span className="featured-category">{featuredPost.category}</span>
                </div>
                <h2>{postTitle(featuredPost.title)}</h2>
                <p>{postExcerpt(featuredPost, 260)}</p>
                <Link to={`/blog/${featuredPost.slug}`} className="btn-primary">
                  Read Full Article <ArrowRight />
                </Link>
              </div>
            </Reveal>
          </div>
        </section>
      )}

      {/* Popular Posts */}
      <section className="popular-posts" aria-label="Popular posts">
        <div className="container">
          <Reveal className="popular-posts-header">
            <div className="popular-posts-title-row">
              <div>
                <p className="popular-posts-eyebrow">Disney Travel Insider</p>
                <h2 className="rule-draw">Latest from the Blog</h2>
              </div>
              <Link to="/blog" className="view-all-link" aria-label="View all blog posts">
                View All <ArrowRight />
              </Link>
            </div>
          </Reveal>

          {isLoading ? (
            <div className="posts-skeleton" aria-label="Loading posts">
              {[1, 2, 3].map(i => <div key={i} className="skeleton-card" aria-hidden="true" />)}
            </div>
          ) : popularPosts.length > 0 ? (
            <div className="home-posts-grid" role="list">
              {popularPosts.map((post, i) => (
                <Reveal key={post.slug || post.id} delay={(i % 3) + 1} role="listitem">
                  <BlogCard post={post} />
                </Reveal>
              ))}
            </div>
          ) : (
            <div className="no-posts">
              <h3>Coming Soon!</h3>
              <p>Chrystal is working on creating amazing Disney travel content for you — tips, resort reviews, dining guides, and magical insights.</p>
            </div>
          )}
        </div>
      </section>

      {/* About Chrystal */}
      <section className="about-chrystal">
        <div className="container">
          <div className="about-content">
            <div className="about-image">
              <div className="avatar-placeholder" aria-hidden="true">CH</div>
            </div>
            <div className="about-text">
              <h2>About Chrystal</h2>
              <p>
                Hi there! I'm Chrystal Harris, your dedicated Disney travel consultant.
                I've been helping families create magical memories at Disney destinations
                for over a decade, and I absolutely love what I do!
              </p>
              <p>
                There's nothing more rewarding than seeing the joy on a child's face
                when they meet their favorite character, or helping couples plan their
                perfect Disney honeymoon. Every vacation is unique, and I'm here to
                make sure yours is absolutely perfect.
              </p>
              <Link to="/about" className="btn-secondary">Learn More About Me</Link>
            </div>
          </div>
        </div>
      </section>

      <SubscribeForm />
    </div>
  );
};

export default Home;
